import React, { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { OnboardingAPI } from "../services/onboarding.service";
import { logger } from "../utils/logger";

/**
 * RequireAdmin — route guard that restricts access to clinic Admins.
 *
 * Sits *inside* RequireAuth (which has already validated the session),
 * then performs a lightweight role check via OnboardingAPI.getMe(). Non-admin
 * roles are redirected to /patients with a toast.
 *
 * Defense-in-depth: the backend already enforces admin access on
 * /dashboard/metrics and /reports/* via _require_admin, so this guard
 * prevents non-admins from even reaching the page (better UX than a
 * post-render 403 banner).
 */

const ADMIN_ROLE = "Admin";

const RequireAdmin: React.FC = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<"checking" | "allowed" | "denied">("checking");

  useEffect(() => {
    let cancelled = false;
    OnboardingAPI.getMe()
      .then(({ role }) => {
        if (cancelled) return;
        if (role === ADMIN_ROLE) {
          setState("allowed");
        } else {
          setState("denied");
          toast.error("Admin access required");
          navigate("/patients", { replace: true });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        // getMe failed — RequireAuth would normally catch auth failures, so this
        // is more likely a transient network error. Be conservative: deny and let
        // RequireAuth re-validate on the redirect target.
        logger.error("RequireAdmin: getMe() failed", err instanceof Error ? err : undefined);
        setState("denied");
        navigate("/patients", { replace: true });
      });
    return () => { cancelled = true; };
  }, [navigate]);

  if (state === "checking") return null;
  if (state === "denied") return null;
  return <Outlet />;
};

export default RequireAdmin;
